import {
  emailSchema,
  LOCALES,
  type Locale,
  MAX_CC_EMAILS,
  PRODUCT_IDS,
  PRODUCTS,
  type ProductId,
  timeZoneSchema,
  usernameSchema,
} from '@bb/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCurrentUser,
  useDeleteAccount,
  useSetProducts,
  useUpdateAccount,
} from '@/features/auth/use-current-user';
import { bookingSettingsQuery, useUpdateBookingSettings } from '@/features/booking/api';
import { ApiRequestError } from '@/lib/api';
import {
  inputCls as baseInput,
  btnPrimary as primaryBtn,
  btnSecondary as secondaryBtn,
} from '@/lib/ui';

const TABS = ['profile', 'notifications', 'products', 'delete'] as const;
type Tab = (typeof TABS)[number];

export const Route = createFileRoute('/app/settings/')({
  /*
    The tab is in the URL so it can be linked to and survives a reload, and so
    the bar in the layout above can read it without either side owning state
    the other needs. An unknown value is dropped rather than rejected: a stale
    link should land on Settings, not on an error.
  */
  validateSearch: (s: Record<string, unknown>): { tab?: Tab } =>
    typeof s.tab === 'string' && (TABS as readonly string[]).includes(s.tab)
      ? { tab: s.tab as Tab }
      : {},
  component: SettingsPage,
});

const TIMEZONES = Intl.supportedValuesOf('timeZone');

function SettingsPage() {
  const { data: user } = useCurrentUser();
  const { tab = 'profile' } = Route.useSearch();
  if (!user) return null;
  return (
    <div className="space-y-10">
      {tab === 'profile' ? (
        <>
          {/*
            First, because it answers the question people arrive with — which
            account is this? — and it is one line to read rather than fields to
            edit.
          */}
          <ConnectionsSection providers={user.providers} />
          <ProfileSection
            name={user.name}
            organizationName={user.organizationName ?? ''}
            username={user.username ?? ''}
            locale={user.locale}
            timezone={user.timezone}
            email={user.email}
          />
        </>
      ) : null}
      {tab === 'notifications' ? <BookingNotificationsSection /> : null}
      {tab === 'products' ? <ProductsSection enabled={user.enabledProducts} /> : null}
      {tab === 'delete' ? <DangerSection email={user.email} /> : null}
    </div>
  );
}

function ProfileSection(props: {
  name: string;
  organizationName: string;
  username: string;
  locale: Locale;
  timezone: string;
  email: string;
}) {
  const { t } = useTranslation(['account', 'common', 'errors']);
  const update = useUpdateAccount();
  const [name, setName] = useState(props.name);
  const [organizationName, setOrganizationName] = useState(props.organizationName);
  const [username, setUsername] = useState(props.username);
  const [locale, setLocale] = useState<Locale>(props.locale);
  const [timezone, setTimezone] = useState(props.timezone);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const input: Record<string, unknown> = {};
    if (name !== props.name) input.name = name;
    if (organizationName !== props.organizationName) input.organizationName = organizationName;
    if (username !== props.username) {
      if (!usernameSchema.safeParse(username).success) return setError('invalid_username');
      input.username = username;
    }
    if (locale !== props.locale) input.locale = locale;
    if (timezone !== props.timezone) {
      if (!timeZoneSchema.safeParse(timezone).success) return setError('invalid_timezone');
      input.timezone = timezone;
    }
    if (Object.keys(input).length === 0) return;
    update.mutate(input, {
      onError: (err) => setError(err instanceof ApiRequestError ? err.code : 'generic'),
    });
  };

  const publicUrl = `${window.location.origin}/${username || '…'}`;

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <h2 className="text-base font-medium">{t('account:profile.title')}</h2>
      <Field label={t('account:profile.email')}>
        <input value={props.email} disabled className={inputCls} />
      </Field>
      <Field label={t('account:profile.name')}>
        <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
      </Field>
      <Field
        label={t('account:profile.organizationName')}
        help={t('account:profile.organizationNameHelp')}
      >
        <input
          value={organizationName}
          onChange={(e) => setOrganizationName(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field
        label={t('account:profile.username')}
        help={t('account:profile.usernameHelp', { url: publicUrl })}
      >
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value.toLowerCase())}
          className={inputCls}
          autoCapitalize="none"
          spellCheck={false}
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('common:language.label')}>
          <select
            value={locale}
            onChange={(e) => setLocale(e.target.value as Locale)}
            className={inputCls}
          >
            {LOCALES.map((l) => (
              <option key={l} value={l}>
                {t(`common:language.${l}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('common:timezone.label')}>
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className={inputCls}
          >
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {error ? <p className="text-sm text-red-600">{t(`errors:${error}`)}</p> : null}
      <div className="flex items-center gap-3">
        <button type="submit" disabled={update.isPending} className={primaryBtn}>
          {t('common:actions.save')}
        </button>
        {update.isSuccess && !update.isPending ? (
          <span className="text-sm text-neutral-500">{t('common:status.saved')}</span>
        ) : null}
      </div>
    </form>
  );
}

/**
 * Extra addresses copied on booking emails to the host, for a shared support
 * or reception inbox. Deliberately not applied to booker-facing mail: the
 * customer should never see an internal address.
 */
function BookingNotificationsSection() {
  const { t } = useTranslation(['account', 'common', 'errors']);
  const { data: settings } = useQuery(bookingSettingsQuery);
  const update = useUpdateBookingSettings();
  // Null until the host edits, so the loaded value shows without a flicker.
  const [rows, setRows] = useState<string[] | null>(null);
  const [verify, setVerify] = useState<boolean | null>(null);
  const [invites, setInvites] = useState<boolean | null>(null);
  // undefined until edited; null is a real value here and means "auto".
  const [hostReminders, setHostReminders] = useState<boolean | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const stored = settings?.ccEmails ?? [];
  const current = rows ?? stored;
  const cleaned = current.map((r) => r.trim()).filter(Boolean);
  const verifyDirty = verify !== null && verify !== (settings?.verifyBookerEmail ?? false);
  const invitesDirty = invites !== null && invites !== (settings?.sendProviderInvites ?? true);
  const storedReminders = settings?.hostReminders ?? null;
  const remindersDirty = hostReminders !== undefined && hostReminders !== storedReminders;
  const dirty =
    (rows !== null && cleaned.join(',') !== stored.join(',')) ||
    verifyDirty ||
    invitesDirty ||
    remindersDirty;
  const canAdd = current.length < MAX_CC_EMAILS;

  const edit = (index: number, value: string) =>
    setRows(current.map((row, i) => (i === index ? value : row)));
  const remove = (index: number) => setRows(current.filter((_, i) => i !== index));

  const save = () => {
    setError(null);
    if (cleaned.some((address) => !emailSchema.safeParse(address).success)) {
      setError('validation');
      return;
    }
    update.mutate(
      {
        ccEmails: cleaned,
        verifyBookerEmail: verify ?? settings?.verifyBookerEmail ?? false,
        sendProviderInvites: invites ?? settings?.sendProviderInvites ?? true,
        hostReminders: hostReminders === undefined ? storedReminders : hostReminders,
      },
      {
        onSuccess: () => {
          setRows(null);
          setVerify(null);
          setInvites(null);
          setHostReminders(undefined);
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        },
        onError: (err: unknown) => setError(err instanceof ApiRequestError ? err.code : 'generic'),
      },
    );
  };

  return (
    <section className="space-y-3">
      <h2 className="text-base font-medium">{t('account:bookingEmails.title')}</h2>
      <p className="text-sm text-neutral-600">{t('account:bookingEmails.body')}</p>

      {current.length > 0 ? (
        <ul className="space-y-2">
          {current.map((row, index) => (
            // Rows have no identity of their own, and reordering is not
            // possible here, so the index is a stable enough key.
            // biome-ignore lint/suspicious/noArrayIndexKey: no stable id exists
            <li key={index} className="flex items-center gap-2">
              <input
                type="email"
                value={row}
                onChange={(e) => edit(index, e.target.value)}
                placeholder={t('account:bookingEmails.placeholder')}
                aria-label={t('account:bookingEmails.label')}
                autoComplete="off"
                className={baseInput}
              />
              <button
                type="button"
                onClick={() => remove(index)}
                aria-label={t('account:bookingEmails.remove')}
                className="flex size-11 shrink-0 items-center justify-center rounded text-neutral-500 hover:bg-neutral-100 sm:size-9"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-neutral-500">{t('account:bookingEmails.none')}</p>
      )}

      <button
        type="button"
        onClick={() => setRows([...current, ''])}
        disabled={!canAdd}
        className={secondaryBtn}
      >
        {t('account:bookingEmails.add')}
      </button>
      {!canAdd ? (
        <p className="text-xs text-neutral-500">
          {t('account:bookingEmails.max', { count: MAX_CC_EMAILS })}
        </p>
      ) : null}

      <p className="text-xs text-neutral-500">{t('account:bookingEmails.help')}</p>

      {/*
        On by default, and the reason a customer's calendar fills in on its own.
        Turning it off is a real choice — some hosts would rather not put a
        customer's address on a calendar entry — so it says what is lost.
      */}
      <label className="flex items-start gap-2 pt-2 text-sm">
        <input
          type="checkbox"
          checked={invites ?? settings?.sendProviderInvites ?? true}
          onChange={(e) => setInvites(e.target.checked)}
          className="mt-0.5 size-4"
        />
        <span>
          {t('account:bookingEmails.invitesLabel')}
          <span className="mt-0.5 block text-xs text-neutral-500">
            {t('account:bookingEmails.invitesHelp')}
          </span>
        </span>
      </label>

      {/*
        Three states, so a select rather than a checkbox. The default is the
        middle answer — remind the staff member only when their own calendar
        is not going to — and the other two are the host overriding it.
      */}
      <div className="pt-2 text-sm">
        <label htmlFor="host-reminders" className="block font-medium">
          {t('account:bookingEmails.hostRemindersLabel')}
        </label>
        <select
          id="host-reminders"
          value={
            (hostReminders === undefined ? storedReminders : hostReminders) === null
              ? 'auto'
              : (hostReminders === undefined ? storedReminders : hostReminders)
                ? 'always'
                : 'never'
          }
          onChange={(e) =>
            setHostReminders(
              e.target.value === 'auto' ? null : e.target.value === 'always' ? true : false,
            )
          }
          className={`${baseInput} mt-1 sm:max-w-sm`}
        >
          <option value="auto">{t('account:bookingEmails.hostRemindersAuto')}</option>
          <option value="always">{t('account:bookingEmails.hostRemindersAlways')}</option>
          <option value="never">{t('account:bookingEmails.hostRemindersNever')}</option>
        </select>
        <p className="mt-1 text-xs text-neutral-500">
          {t('account:bookingEmails.hostRemindersHelp')}
        </p>
      </div>

      <label className="flex items-start gap-2 pt-2 text-sm">
        <input
          type="checkbox"
          checked={verify ?? settings?.verifyBookerEmail ?? false}
          onChange={(e) => setVerify(e.target.checked)}
          className="mt-0.5 size-4"
        />
        <span>
          {t('account:bookingEmails.verifyLabel')}
          <span className="mt-0.5 block text-xs text-neutral-500">
            {t('account:bookingEmails.verifyHelp')}
          </span>
        </span>
      </label>

      {error ? <p className="text-sm text-red-600">{t(`errors:${error}`)}</p> : null}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || update.isPending}
          className={primaryBtn}
        >
          {t('common:actions.save')}
        </button>
        {saved ? <span className="text-sm text-green-700">{t('common:status.saved')}</span> : null}
      </div>
    </section>
  );
}

function ProductsSection({ enabled }: { enabled: ProductId[] }) {
  const { t } = useTranslation(['account', 'common']);
  const setProducts = useSetProducts();
  const toggle = (id: ProductId) => {
    const next = enabled.includes(id) ? enabled.filter((p) => p !== id) : [...enabled, id];
    if (next.length === 0) return;
    setProducts.mutate({ enabledProducts: next });
  };
  return (
    <section className="space-y-3">
      <h2 className="text-base font-medium">{t('account:products.title')}</h2>
      <p className="text-sm text-neutral-600">{t('account:products.help')}</p>
      <ul className="divide-y divide-neutral-200 rounded-lg border border-neutral-200 bg-white">
        {PRODUCT_IDS.map((id) => {
          const product = PRODUCTS[id];
          const on = enabled.includes(id);
          const available = product.status === 'available';
          return (
            <li key={id} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <div className="text-sm font-medium">{t(`common:products.${id}.name`)}</div>
                <div className="text-xs text-neutral-500">{t(`common:products.${id}.tagline`)}</div>
              </div>
              {available ? (
                <button
                  type="button"
                  onClick={() => toggle(id)}
                  disabled={setProducts.isPending || (on && enabled.length === 1)}
                  title={on && enabled.length === 1 ? t('account:products.lastProduct') : undefined}
                  className={on ? secondaryBtn : primaryBtn}
                >
                  {on ? t('account:products.enabled') : t('account:products.enable')}
                </button>
              ) : (
                <span className="text-xs text-neutral-400">{t('common:status.comingSoon')}</span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ConnectionsSection({ providers }: { providers: string[] }) {
  const { t } = useTranslation(['account', 'auth']);
  return (
    <section className="space-y-2">
      <h2 className="text-base font-medium">{t('account:connections.title')}</h2>
      <p className="text-sm text-neutral-600">
        {providers
          .map((p) => t('account:connections.signedInWith', { provider: t(`auth:providers.${p}`) }))
          .join(', ')}
      </p>
    </section>
  );
}

function DangerSection({ email }: { email: string }) {
  const { t } = useTranslation(['account', 'common']);
  const del = useDeleteAccount();
  const [confirm, setConfirm] = useState('');
  return (
    <section className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-4">
      <h2 className="text-base font-medium text-red-700">{t('account:danger.title')}</h2>
      <p className="text-sm text-red-700/80">{t('account:danger.body')}</p>
      <Field label={t('account:danger.confirmLabel')}>
        <input value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
      </Field>
      <button
        type="button"
        disabled={confirm.trim().toLowerCase() !== email || del.isPending}
        onClick={() => del.mutate(confirm)}
        className="inline-flex min-h-11 items-center justify-center rounded bg-red-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40 sm:min-h-0 sm:py-1.5"
      >
        {t('account:danger.button')}
      </button>
    </section>
  );
}

function Field({
  label,
  help,
  children,
}: {
  label: string;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the control is passed as children and wrapped by the label
    <label className="block">
      <span className="mb-1 block text-sm font-medium">{label}</span>
      {children}
      {help ? <span className="mt-1 block text-xs text-neutral-500">{help}</span> : null}
    </label>
  );
}

const inputCls = `${baseInput} disabled:bg-neutral-100 disabled:text-neutral-500`;
